import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Zap, Info, Moon, Sun, X, RefreshCw } from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ToolsNav from "../components/ToolsNav";
import QuickConvert from "../components/QuickConvert";
import { RenderCMSContent } from "../components/CMSRenderer";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

function ConvertJpgToWebp({ embedded = false }) {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [quality, setQuality] = useState([85]);
  const [pageContent, setPageContent] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const SOURCE_FORMAT = "JPG";
  const TARGET_FORMAT = "WebP";
  const ACCEPT_TYPES = ".jpg,.jpeg,image/jpeg";

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const updateSEOTags = (seo, fallbackTitle) => {
    document.title = seo?.meta_title || fallbackTitle || `Convert ${SOURCE_FORMAT} to ${TARGET_FORMAT} - KayTool`;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', seo?.meta_description || `Free online ${SOURCE_FORMAT} to ${TARGET_FORMAT} converter. Convert your images instantly in your browser.`);
  };

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/convert-jpg-to-webp`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
          updateSEOTags(data.seo, data.title);
        }
      } catch (error) {
        updateSEOTags(null, `Convert ${SOURCE_FORMAT} to ${TARGET_FORMAT} - KayTool`);
      }
    };
    if (!embedded) fetchPageContent();
  }, [embedded]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type === 'image/jpeg' || file.name.toLowerCase().endsWith('.jpg') || file.name.toLowerCase().endsWith('.jpeg')
    );
    if (files.length > 0) processMultipleImages(files);
    else toast.error(`Please drop ${SOURCE_FORMAT} files only`);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) processMultipleImages(files);
    e.target.value = '';
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type === 'image/jpeg') {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(new File([file], `pasted-image-${Date.now()}.jpg`, { type: file.type }));
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      processMultipleImages(imageFiles);
      toast.success(`${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''} pasted!`);
    }
  };

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { resolve({ width: img.width, height: img.height }); URL.revokeObjectURL(img.src); };
      img.src = URL.createObjectURL(file);
    });
  };

  const convertImage = async (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob((blob) => {
          const convertedFile = new File([blob], file.name.replace(/\.(jpg|jpeg)$/i, '.webp'), { type: 'image/webp' });
          resolve(convertedFile);
        }, 'image/webp', quality[0] / 100);
        
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const processMultipleImages = async (files) => {
    setIsProcessing(true);
    setCompressionProgress(0);
    const newImages = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setCompressionProgress(Math.round(((i) / files.length) * 100));
      try {
        const dimensions = await getImageDimensions(file);
        const converted = await convertImage(file);
        newImages.push({
          id: Date.now() + i,
          name: file.name,
          originalFile: file,
          originalSize: file.size,
          dimensions: dimensions,
          convertedFile: converted,
          convertedSize: converted.size,
          preview: URL.createObjectURL(converted),
          originalPreview: URL.createObjectURL(file),
        });
      } catch (error) {
        toast.error(`Failed to convert ${file.name}`);
      }
    }
    setImages(prev => [...prev, ...newImages]);
    setCompressionProgress(100);
    setIsProcessing(false);
    if (newImages.length > 0) toast.success(`${newImages.length} image${newImages.length > 1 ? 's' : ''} converted to ${TARGET_FORMAT}!`);
  };

  const downloadImage = (image) => {
    const link = document.createElement("a");
    link.href = image.preview;
    link.download = image.name.replace(/\.(jpg|jpeg)$/i, '.webp');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllAsZip = async () => {
    if (images.length === 0) return;
    const zip = new JSZip();
    for (const image of images) {
      const response = await fetch(image.preview);
      const blob = await response.blob();
      zip.file(image.name.replace(/\.(jpg|jpeg)$/i, '.webp'), blob);
    }
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `converted-webp-images.zip`);
    toast.success("Downloaded all images as ZIP!");
  };

  const removeImage = (id) => setImages(prev => prev.filter(img => img.id !== id));
  const clearAll = () => { setImages([]); toast.success("All images cleared"); };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (embedded) return <div className="p-4"><p className="text-center text-slate-500">Convert {SOURCE_FORMAT} to {TARGET_FORMAT} Tool</p></div>;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-slate-50 to-purple-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800 dark:text-white">{SOURCE_FORMAT} to {TARGET_FORMAT}</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">by KayTool</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <QuickConvert />
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 border-purple-200 dark:border-purple-800 text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20">
              <RefreshCw className="w-3 h-3" /> {SOURCE_FORMAT} → {TARGET_FORMAT}
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="text-slate-600 dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Ad Placement - Above the Fold (Below Header) */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white mb-3">Convert {SOURCE_FORMAT} to {TARGET_FORMAT}</h2>
          <p className="text-slate-600 dark:text-slate-300 max-w-xl mx-auto">Drop your {SOURCE_FORMAT} images and convert them to {TARGET_FORMAT} format. WebP offers better compression with excellent quality.</p>
        </div>

        <div className="flex justify-center mb-6">
          <Card className="p-4 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 w-full max-w-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Quality</span>
              <span className="text-sm font-bold text-purple-600 dark:text-purple-400">{quality[0]}%</span>
            </div>
            <Slider value={quality} onValueChange={setQuality} min={10} max={100} step={5} className="w-full" />
          </Card>
        </div>

        <div
          data-testid="upload-zone"
          className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer
            ${isDragging ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 scale-[1.02]' : 'border-slate-300 dark:border-slate-600 hover:border-purple-400 bg-white dark:bg-slate-800'}`}
          onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}
        >
          <input ref={fileInputRef} type="file" accept={ACCEPT_TYPES} multiple onChange={handleFileSelect} className="hidden" />
          <div className="flex flex-col items-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDragging ? 'bg-purple-100 dark:bg-purple-900/40' : 'bg-slate-100 dark:bg-slate-700'}`}>
              <Upload className={`w-8 h-8 ${isDragging ? 'text-purple-500' : 'text-slate-400'}`} />
            </div>
            <div>
              <p className="text-lg font-medium text-slate-700 dark:text-slate-200">Drop, paste, or click to upload {SOURCE_FORMAT} images</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Ctrl+V to paste • Multiple files supported</p>
            </div>
            <Badge className="bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">Convert to {TARGET_FORMAT}</Badge>
          </div>
        </div>

        {isProcessing && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600 dark:text-slate-300">Converting images...</span>
              <span className="text-sm font-medium text-purple-600">{compressionProgress}%</span>
            </div>
            <Progress value={compressionProgress} className="h-2" />
          </div>
        )}

        {images.length > 0 && (
          <div className="mt-8 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Converted Images ({images.length})</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearAll} className="text-red-600 hover:bg-red-50"><X className="w-4 h-4 mr-1" /> Clear All</Button>
                {images.length > 1 && <Button size="sm" onClick={downloadAllAsZip} className="bg-purple-600 hover:bg-purple-700"><Download className="w-4 h-4 mr-1" /> Download All (ZIP)</Button>}
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {images.map((image) => (
                <Card key={image.id} className="overflow-hidden bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                  <div className="aspect-video relative bg-slate-100 dark:bg-slate-700">
                    <img src={image.preview} alt={image.name} className="w-full h-full object-contain" />
                    <button onClick={() => removeImage(image.id)} className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"><X className="w-4 h-4" /></button>
                    <Badge className="absolute bottom-2 left-2 bg-purple-500">{TARGET_FORMAT}</Badge>
                  </div>
                  <CardContent className="p-4">
                    <p className="font-medium text-slate-800 dark:text-white truncate text-sm">{image.name}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 dark:text-slate-400">
                      <span>{formatFileSize(image.originalSize)}</span><span>→</span>
                      <span className="text-purple-600 font-medium">{formatFileSize(image.convertedSize)}</span>
                      <span className="ml-auto text-purple-600">-{Math.round((1 - image.convertedSize / image.originalSize) * 100)}%</span>
                    </div>
                    <Button onClick={() => downloadImage(image)} className="w-full mt-3 bg-purple-600 hover:bg-purple-700" size="sm"><Download className="w-4 h-4 mr-1" /> Download</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <Card className="p-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <Zap className="w-8 h-8 text-purple-500 mb-3" /><h3 className="font-semibold text-slate-800 dark:text-white">Smaller Files</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">WebP offers 25-35% better compression</p>
          </Card>
          <Card className="p-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <RefreshCw className="w-8 h-8 text-purple-500 mb-3" /><h3 className="font-semibold text-slate-800 dark:text-white">Web Optimized</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Perfect for websites</p>
          </Card>
          <Card className="p-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <Info className="w-8 h-8 text-purple-500 mb-3" /><h3 className="font-semibold text-slate-800 dark:text-white">100% Private</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Browser-only processing</p>
          </Card>
        </div>

        {/* Ad Placement - Before Features */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      {pageContent?.content && pageContent.content.length > 0 && (
        <div className="bg-slate-50 dark:bg-slate-800/50 py-12">
          <div className="max-w-6xl mx-auto px-4"><RenderCMSContent content={pageContent.content} /></div>
        </div>
      )}

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-slate-500 dark:text-slate-400">
          <p>© 2025 KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>
    </div>
  );
}

export default ConvertJpgToWebp;
